/**
 * Deterministic pseudo-random numbers.
 *
 * The simulation has to produce the same world from the same seed on the
 * server and on any client that replays it, so nothing in the sim is allowed
 * to call Math.random(). Everything draws from one of these.
 *
 * mulberry32: 32-bit state, period 2^32, passes gjrand's basic suite. Fast,
 * tiny, and good enough for traffic that is not cryptography.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    // Mix the seed so that 1, 2, 3 do not produce visibly related streams.
    this.state = (seed ^ 0x9e3779b9) >>> 0;
  }

  /** Uniform in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Uniform integer in [0, n). Returns 0 when n is not positive. */
  int(n: number): number {
    if (n <= 0) return 0;
    return Math.floor(this.next() * n) | 0;
  }

  /**
   * A draw from a normal distribution, by the Box-Muller transform.
   *
   * Needed because most of the variation in a person's day is not uniform.
   * Somebody who leaves for work at eight leaves at eight most days, a little
   * before or after it often, and an hour late rarely. A uniform jitter would
   * make the hour-late morning exactly as common as the on-time one, which is
   * both wrong and, for a game about predicting people, wrong in the direction
   * that matters.
   */
  normal(mean = 0, sd = 1): number {
    // u must not be zero, or the log diverges.
    let u = 0;
    while (u === 0) u = this.next();
    const v = this.next();
    return mean + sd * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  /** True with the given probability. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** A uniformly chosen element. Throws on an empty list rather than
   *  returning undefined, because every caller here treats that as a bug. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick on an empty list');
    return items[this.int(items.length)] as T;
  }

  /**
   * Index into `weights` chosen in proportion to the weights. Used for road
   * class preference when a vehicle picks a turn.
   */
  weighted(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) total += w;
    if (total <= 0) return this.int(weights.length);
    let roll = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      roll -= weights[i] as number;
      if (roll <= 0) return i;
    }
    return weights.length - 1;
  }

  /** Current internal state, so a run can be checkpointed and resumed. */
  save(): number {
    return this.state;
  }

  restore(state: number): void {
    this.state = state >>> 0;
  }
}
